<template>
    <div class="player" ref="playerr">
        <div class="player__video">
            <video ref="video" disablePictureInPicture controlsList="nodownload" :muted="audio==='muted' || audio==='off'" :poster="poster" playsinline webkit-playsinline></video>
        </div>
        <div v-if="status=='limbo'" class="player__play" @click="video && video.play()">
            <i class="fa-solid fa-play"></i>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { onBeforeUnmount, onMounted, ref, watch, type Ref, nextTick } from 'vue';
import { Player } from '@/components/janus/player';
import notifications from '@/socket';
import { seconds } from '@/utils';

type AudioOptions = "off" | "muted" | "on";

const props = defineProps<{
    name: string;
    token: string;
    server: string;
    attempt: number;
    audio?: AudioOptions;
    poster?: string;
    debug: boolean;
}>();

const playerr = ref<HTMLDivElement | null>(null);

const emit = defineEmits<{
    (e: 'statusChange', value: string, error?: string): void,
    (e: 'update:audio', value:AudioOptions, oldValue:AudioOptions): void,
    (e: 'metadata', value:{ width:number, height:number} ): void
}>();

watch( ()=>props.audio,  (value)=>{
    //when the initial play attempt fails because it's not allowed by the browser bacause the inital video was not muted
    if (status.value == 'limbo'){
        const vid = video.value;
        if (!vid){
            return;
        }

        //the onPlay and onError event handlers are assigned during the first attempt to play the video.
        //playing will resolve, erroring will reject the original promise...
        vid.play();
    }

})

const status = ref('');
const video: Ref<HTMLVideoElement | null> = ref(null);
let player: Player;

onMounted(() => {
    setupVideo();
    startPlayer();
});

watch(
    () => props.attempt,
    async (value: number, oldValue: number) => {
        if(value === oldValue){ return; }
        setupVideo();
        startPlayer();
    }
);

onBeforeUnmount(cleanup);

function setupVideo() {
    const vid = video.value;
    if (!vid) {
        throw new Error('impossible');
    }

    vid.onended = () => {
        addLog({ event: 'videoend' });
        cleanup();
    };

    vid.onloadedmetadata = () => {
        vid.width = vid.videoWidth;
        vid.height = vid.videoHeight;

        //if the video is there, the container is obviously there also.
        const p = playerr.value!
        p.scrollLeft = ( vid.clientWidth -  p.clientWidth) / 2;

        emit("metadata", {width: vid.videoWidth, height: vid.videoHeight})
    };
}

async function startPlayer() {
    try {
        if (player) {
            player.destroy();
            unFitVideo();
            setStatus('reconnecting');
        }

        //Karel 3-7-23 disable for now
        //setTimeout( ()=>startTimeout(), seconds(5) );
        player = new Player(props);
        player.log = addLog;
        player.onError = onError;
        player.onStateChange = setStatus;

        const stream = await player.start();
        setStatus('attaching_stream');
        attachStream(stream);
        await startVideo();
        setStatus('active');
        flushLogs();
    } catch (error) {
        if (error instanceof Error) {
            onError(`${error.name} ${error.message}`);
        } else {
            onError(error);
        }

        cleanup();
    }
}

function startTimeout(){
    //only timeouts during specific statuses are 'valid'
    if ( ['active', 'destroying', 'destroying_janus', 'limbo'].includes( status.value ) ){
        return;
    }
    onError( "startTimeout");
    cleanup();
}

const logs: any[] = [];

function addLog(item: { event: string; [rest: string]: any }) {
    if (props.debug) console.log(item);
    //let's first replace all spaces in the properties..
    for (const prop in item) {
        if (typeof item[prop] !== 'string') {
            continue;
        }

        if (item[prop].indexOf(' ') === -1) {
            continue;
        }

        item[prop] = item[prop].replace(/ /g, '-');
    }

    item.attempt = props.attempt;
    item.server = props.server.split('.')[0];

    item.t = Date.now();
    logs.push(item);
}

function onError(error: any) {
    setStatus('error', error);
    //console.log(`Janus error!: ${error}`);
}

function unFitVideo() {
    video.value && (video.value.style.transform = '');
}

function attachStream(stream: MediaStream) {
    if (!video.value) {
        return;
    }

    const attach = props.audio == 'off' ?  new MediaStream( stream.getVideoTracks() ) : stream;
    try {
        video.value.srcObject = attach;
    } catch (e) {
        //this is the deprecated way, casting as any to stop the compiler from nagging
        video.value.src = URL.createObjectURL(attach as any);
    }
}

async function startVideo() {
    return new Promise<void>(async (resolve, reject) => {
        if (!video.value) {
            return;
        }

        const vid = video.value;

        vid.scrollIntoView({
            block: 'center',
            inline: 'center'
        });
        //onPlaying the video is actually playing. And only then we register the 'active'-ness of the component
        vid.onplaying = () => {
            resolve();
            vid.onplaying = vid.onerror = null;
        };

        //catch an error that occurs during the starting of the playback, in order to be able to reject this promise.
        vid.onerror = error => {
            reject(error);
            vid.onplaying = vid.onerror = null;
        };

        //detect if the browser blocks autoplaying of unmuted content
        try {
            await vid.play();
        } catch (error: any) {
            if (error.name === 'NotAllowedError') {
                addLog({ event: 'startNotAllowed' });
                setStatus('limbo');
                emit('update:audio', "muted", "off");
            } else {
                reject(error);
            }
        }
    });
}

function flushLogs() {
    if (!logs.length) {
        return;
    }

    //flushing the logs..
    //first add the first 5 characters of the room to each log line, add a 'scope' of 'cam' to each line.
    //also, log the 'opaque id' which identifies the janus the player instance uniquely
    logs.forEach(log => {
        log.r = props.name.substring(0, 5);
        log.s = 'cam';
        log.id = props.token;
    });

    notifications.sendEvent({
        content: logs,
        event: 'udplog',
        receiverType: undefined
    });

    logs.splice(0, logs.length);
}

function setStatus(value: string, error?:string) {
    if (value === status.value) {
        return;
    }

    const oldValue = status.value;
    status.value = value;
    emit('statusChange', value, error);
    addLog({ event: 'statechange', value, oldValue, error });
}

function cleanup() {
    if (status.value == 'destroying') {
        return;
    }

    setStatus('destroying');

    try {
        const vid = video.value;
        if (vid) {
            vid.onended = vid.onplaying = vid.onloadedmetadata = vid.onerror = null;
            vid.pause();
            vid.srcObject = null;
        }
        if (player) {
            player.destroy();
        }
    } finally {
        flushLogs();
    }
}
</script>

<style lang="scss">
    @use '@/styles/_mixins.scss' as *;

    .player {
        position: relative;
        display: flex;
        width: 100%;
        height: 100%;

        &__video {
            display: flex;
            width: 100%;
            height: 100%;
        }

        &__play {
            position: absolute;
            z-index: 4;
            left: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            height: 100%;
            cursor: pointer;
            i {
                display: flex;
                @include rem(padding, 15px 20px);
                @include rem(font-size, 42px);
                @include border-bottom-radius(5px);
                color: #fff;
                background-color: rgba(0, 0, 0, 0.7);

            }
        }

        &.scroll {
            display: flex;
            position: absolute;
            width: 100%;
            height: 100%;
            overflow-x: scroll;
            overflow-y: hidden;
            align-items: center;
            justify-content: space-between;
            .player__video {
                position: relative;
                width: 100%;
                height: 100%;
                video {
                    height: 100%;
                    width: auto;
                    object-fit: contain;
                }
            }
            .player__play{
                position: relative;
                width: 100%;
                height: 100%;
            }
        }
    }
</style>
